import React from 'react';
import { Edit, Power, PowerOff, Ruler } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Pagination } from '@/components/ui/pagination';
import { SortableHeader } from '@/components/ui/sortable-header';
import { useTableData } from '@/hooks/useTableData';
import type { UnitOfMeasure } from '../types/system-parameters.types';

interface UnitsTableProps {
  units: UnitOfMeasure[];
  onEdit: (unit: UnitOfMeasure) => void;
  onDelete: (unit: UnitOfMeasure) => void;
}

type SortField = 'name' | 'displayName' | 'category' | 'sortOrder';

export const UnitsTable: React.FC<UnitsTableProps> = ({
  units,
  onEdit,
  onDelete
}) => {
  const fieldLabels: Record<SortField, string> = {
    name: 'Nombre',
    displayName: 'Nombre Display',
    category: 'Categoría',
    sortOrder: 'Orden'
  };

  const sortValueExtractor = (unit: UnitOfMeasure, field: SortField): any => {
    switch (field) {
      case 'name':
        return unit.additionalData.name.toLowerCase();
      case 'displayName':
        return unit.additionalData.displayName.toLowerCase();
      case 'category':
        return unit.additionalData.category.toLowerCase();
      case 'sortOrder':
        return unit.additionalData.sortOrder;
      default:
        return unit.additionalData.name;
    }
  };

  const tableData = useTableData<UnitOfMeasure, SortField>(
    {
      data: units,
      defaultSortField: 'sortOrder',
      defaultSortDirection: 'asc',
      itemsPerPage: 10,
      sortValueExtractor
    },
    fieldLabels
  );

  if (units.length === 0) {
    return (
      <div className="text-center py-12">
        <h3 className="text-lg font-medium text-gray-900 mb-2">No hay unidades de medida</h3>
        <p className="text-gray-500">No se encontraron unidades con los filtros aplicados.</p>
      </div>
    );
  }

  return (
    <div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b">
            <tr>
              <SortableHeader
                field="name"
                onSort={tableData.handleSort}
                getSortIcon={tableData.getSortIcon}
                className="text-left"
              >
                Nombre
              </SortableHeader>
              <SortableHeader
                field="displayName"
                onSort={tableData.handleSort}
                getSortIcon={tableData.getSortIcon}
                className="text-left"
              >
                Nombre Display
              </SortableHeader>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                Simbolo
              </th>
              <SortableHeader
                field="category"
                onSort={tableData.handleSort}
                getSortIcon={tableData.getSortIcon}
                className="text-center"
              >
                Categoria
              </SortableHeader>
              <SortableHeader
                field="sortOrder"
                onSort={tableData.handleSort}
                getSortIcon={tableData.getSortIcon}
                className="text-center"
              >
                Orden
              </SortableHeader>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                Estado
              </th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                Acciones
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {tableData.pagination.paginatedData.map((unit) => (
              <tr key={unit.id} className="hover:bg-gray-50">
                <td className="px-4 py-4">
                  <div className="flex items-center gap-2">
                    <Ruler className="h-4 w-4 text-gray-500" />
                    <div className="text-sm font-medium text-gray-900">
                      {unit.additionalData.name}
                    </div>
                  </div>
                </td>

                <td className="px-4 py-4">
                  <div className="text-sm text-gray-600">
                    {unit.additionalData.displayName}
                  </div>
                </td>

                <td className="px-4 py-4 text-center">
                  <span className="text-sm font-mono text-gray-600">
                    {unit.additionalData.symbol || '-'}
                  </span>
                </td>

                <td className="px-4 py-4 text-center">
                  <Badge variant="outline">
                    {unit.additionalData.category || 'Sin categoría'}
                  </Badge>
                </td>

                <td className="px-4 py-4 text-center">
                  <Badge variant="outline" className="font-mono">
                    {unit.additionalData.sortOrder}
                  </Badge>
                </td>

                <td className="px-4 py-4 text-center">
                  <Badge className={unit.additionalData.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}>
                    {unit.additionalData.isActive ? 'Activa' : 'Inactiva'}
                  </Badge>
                </td>

                <td className="px-4 py-4 text-center">
                  <div className="flex gap-2 justify-center">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onEdit(unit)}
                      className="text-xs"
                    >
                      <Edit className="w-3 h-3 mr-1" />
                      Editar
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onDelete(unit)}
                      className={`text-xs ${unit.additionalData.isActive
                        ? 'text-orange-600 hover:text-orange-700 hover:bg-orange-50'
                        : 'text-green-600 hover:text-green-700 hover:bg-green-50'}`}
                    >
                      {unit.additionalData.isActive ? (
                        <>
                          <PowerOff className="w-3 h-3 mr-1" />
                          Desactivar
                        </>
                      ) : (
                        <>
                          <Power className="w-3 h-3 mr-1" />
                          Reactivar
                        </>
                      )}
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Sort info */}
      <div className="mt-2 text-sm text-gray-500">
        Ordenado por: <span className="font-medium">
          {tableData.sortInfo.label}
        </span> ({tableData.sortInfo.direction === 'asc' ? 'Ascendente' : 'Descendente'})
      </div>

      {/* Pagination */}
      <Pagination
        currentPage={tableData.pagination.currentPage}
        totalPages={tableData.pagination.totalPages}
        onPageChange={tableData.pagination.goToPage}
        showingFrom={tableData.pagination.showingFrom}
        showingTo={tableData.pagination.showingTo}
        totalItems={tableData.pagination.totalItems}
        itemsPerPage={tableData.pagination.itemsPerPage}
        onItemsPerPageChange={tableData.pagination.setItemsPerPage}
      />
    </div>
  );
};
